import { type FC, useMemo } from 'react';
import type { Headers } from 'react-csv/lib/core';
import dayjs from 'dayjs';
import type {
  TObjectImportJob,
  TObjectJob,
  TOperationType,
} from 'types/objectJob.type';
import { Typography } from '@timeedit/ui-components';
import { Spin } from '@timeedit/ui-components/antd';
import useErrorTableData from '../hooks/useErrorTableData';
import CsvDownloadButton, { csvFilename } from './CsvDownloadButton';
import JobErrorTable from './JobErrorTable';
import './JobExecutionResult.scss';

const { Title, Text } = Typography;

type Props = {
  job: TObjectJob;
};

const JobExecutionResult: FC<Props> = (props) => {
  const inProgress = ['QUEUED', 'RUNNING'].includes(props.job.status);

  const summary = props.job.summary;

  const errorData = useErrorTableData(props.job);

  return (
    <>
      <div className="job-execution-result-title">
        <Title level={4}>Result</Title>{' '}
        <CsvDownload job={props.job} enabled={!inProgress} />
      </div>

      {inProgress && (
        <div className="job-execution-result-box">
          <Spin /> <Text>Operation in progress</Text>
        </div>
      )}
      {!inProgress && (
        <div className="job-execution-result-box">
          <table>
            <tbody>
              <tr>
                <td>
                  <Text>Updated objects:</Text>
                </td>
                <td>
                  <Text strong>
                    {summary.successful}/{summary.total}
                  </Text>
                </td>
              </tr>
              <tr>
                <td>
                  <Text>Failed updates:</Text>
                </td>
                <td>
                  <Text strong>{summary.failed}</Text>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
      <JobErrorTable data={errorData} title="Failed updates" />
    </>
  );
};

const CsvDownload: FC<{
  job: TObjectJob;
  enabled: boolean;
}> = (props) => {
  const rows = useMemo(() => toRows(props.job), [props.job]);

  return (
    <CsvDownloadButton
      rows={rows}
      headers={HEADERS(props.job.operationType)}
      filename={csvFilename([
        dayjs(props.job.queuedAt).format('YYYY-MM-DD_HH_mm_ss'),
        props.job.objectTypeName,
        props.job.operationType,
      ])}
      enabled={props.enabled}
    />
  );
};

const toRows = (job: TObjectJob) => {
  const data =
    job.operationType === 'IMPORT' ? toImportRows(job) : toExistingJobRows(job);

  const statusOrder = {
    error: 0,
    unknown: 1,
    unchanged: 2,
    success: 3,
  } as const;

  data.sort((a, b) => {
    const statusA = statusOrder[a.status];
    const statusB = statusOrder[b.status];

    if (statusA !== statusB) return statusA - statusB;

    return (a.extId ?? '').localeCompare(b.extId ?? '');
  });

  return data;
};

export const toExistingJobRows = (
  job: Exclude<TObjectJob, TObjectImportJob>
): Row[] => {
  return job.objectsToProcess.map(({ extId, id }) => {
    const error = job.batchErrors.find((e) => e.objects.includes(id));

    if (error) {
      return {
        extId: extId,
        status: 'error' as const,
        errorCode: error.errorCode,
        message: error.message,
      };
    } else if ('unchangedObjects' in job && job.unchangedObjects.includes(id)) {
      return {
        extId: extId,
        status: 'unchanged' as const,
      };
    } else if (!job.successfulObjects.includes(id)) {
      return {
        extId: extId,
        status: 'unknown' as const,
        message: 'Unknown' as const,
      };
    }

    return {
      extId: extId,
      status: 'success' as const,
    };
  });
};

export const toImportRows = (job: TObjectImportJob): Row[] => {
  type BatchError = TObjectImportJob['batchErrors'][number];
  type FlatError = Omit<BatchError, 'objects'> & {
    extId: string;
    cell?: string;
  };

  const allErrors: Record<string, FlatError[]> = {};

  const addError = (
    extId: string,
    cell: string | undefined = undefined,
    { objects: _, ...error }: BatchError
  ) => {
    allErrors[extId] ??= [];
    allErrors[extId].push({ ...error, extId, cell });
  };

  for (const error of job.batchErrors) {
    if (!error.objects || error.objects.length === 0) continue;

    for (const obj of error.objects) {
      if (typeof obj === 'string') {
        addError(obj, undefined, error);
        continue;
      }
      if (obj.extId == null) continue;
      addError(obj.extId, obj.cell, error);
    }
  }

  return job.objectsToProcess.flatMap<Row>((extId) => {
    const errors = allErrors[extId] ?? [];

    if (errors.length === 0) {
      if (job.unchangedObjects.includes(extId)) {
        return [
          {
            extId: extId,
            status: 'unchanged' as const,
          },
        ];
      } else if (!job.successfulObjects.find((obj) => obj.extId === extId)) {
        return [
          {
            extId: extId,
            status: 'unknown' as const,
            message: 'Unknown' as const,
          },
        ];
      }

      return [
        {
          extId: extId,
          status: 'success' as const,
        },
      ];
    }

    return errors.map((error) => ({
      extId: extId,
      status: 'error' as const,
      errorCode: error.errorCode,
      column: error.column,
      cell: error.cell,
      message: error.message,
    }));
  });
};

const HEADERS = (type: TOperationType): Headers => [
  { key: 'extId', label: 'Ext. ID' },
  { key: 'status', label: 'Status' },
  { key: 'errorCode', label: 'Error Code' },
  ...(type === 'IMPORT'
    ? [
        { key: 'column', label: 'Column' },
        { key: 'cell', label: 'Cell' },
      ]
    : []),
  { key: 'message', label: 'Message' },
];

type Success = {
  extId: string;
  status: 'success';
};

type Unchanged = {
  extId: string;
  status: 'unchanged';
};

type Error = {
  extId: string;
  status: 'error';
  message: string;
  cell?: string;
  column?: string;
};
type Unknown = {
  extId: string;
  status: 'unknown';
  message: string;
};

type Row = Success | Unchanged | Error | Unknown;

export default JobExecutionResult;
